import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from '@tanstack/react-router';
import { Box, Building2, Plus } from 'lucide-react';

import { CreateEstateModal } from '@/components/modals/create-estate-modal';
import { useModal } from '@/components/modals/new/use-modal';
import { UploadFloorPlanModal } from '@/components/modals/upload-floor-plan-modal';
import { Button } from '@/components/new-ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/new-ui/dropdown-menu';

/** "+" menu in the header, opens the existing create estate and floor plan upload modals. */
export const GlobalAdd = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const estateModal = useModal();
  const uploadFloorPlanModal = useModal();

  const onCreateEstateSelect = () => {
    setDropdownOpen(false);
    estateModal.open();
  };

  const onUploadFloorPlanSelect = () => {
    setDropdownOpen(false);
    uploadFloorPlanModal.open();
  };

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button size="icon" className="h-9 w-9 shrink-0" aria-label={t('createNew')}>
            <Plus className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>{t('createNew')}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={onCreateEstateSelect} className="cursor-pointer gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                <Building2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{t('createEstateProject')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('addNewPropertyOrProject')}
                </span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={onUploadFloorPlanSelect} className="cursor-pointer gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
                <Box className="h-4 w-4 text-primary" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-medium">{t('uploadFloorPlan')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('quicklyAddFloorPlanToAnyEstate')}
                </span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateEstateModal navigate={navigate} modal={estateModal} />
      <UploadFloorPlanModal modal={uploadFloorPlanModal} />
    </>
  );
};
